import React from "react";
import { Link } from "../components/PageLink";
import { Helmet } from "react-helmet-async";
import Picture, { type ImgSet } from "../components/Picture";
import { supplierImg } from "../lib/supplierImages";
import Gallery, { type GalleryItem } from "../components/Gallery";
import Testimonials from "../components/Testimonials";
import ProcessStrip from "../components/ProcessStrip";
import CTABand from "../components/CTABand";

const heroImg: ImgSet = supplierImg("j-s-audio-visual-solutions-home-theater-av-installation");

const aboutImg: ImgSet = supplierImg("j-s-audio-visual-solutions-living-room-av-display-installation");

const imgHomeTheater = supplierImg("j-s-audio-visual-solutions-tv-soundbar-installation-living-room");
const imgSoundSystems = supplierImg("j-s-audio-visual-solutions-outdoor-sound-system");
const imgCommercial = supplierImg("j-s-audio-visual-solutions-modern-office-av-installation");

const portraitGalleryItems: GalleryItem[] = [
  { file: "J_S_home_theater_system_install_hopkinton_ma.jpg", alt: "Home theater system installation in Hopkinton, MA by J&S Audio Video Solutions" },
  { file: "J_S_home_theater_fireplace_worcester_ma.jpg", alt: "Television mounted above a fireplace in Worcester, MA by J&S Audio Video Solutions" },
  { file: "471501502_1058531619381024_1699632199117910748_n-restaurant.jpg", alt: "Restaurant audio visual installation by J&S Audio Video Solutions" },
  { file: "488655010_1165412298929263_8422537262457827383_n_audio_system_set_up.jpg", alt: "Multi-zone whole-home audio system installation by J&S Audio Video Solutions" },
  { file: "J_S_TV_installation_services_mendon.jpg", alt: "Television installation in Mendon, MA by J&S Audio Video Solutions" },
  { file: "J_S_outdoor_TV_installation_oakham_ma.jpg", alt: "Outdoor television mounted under a timber pavilion in Oakham, MA by J&S Audio Video Solutions" },
  { file: "485380824_1154458716691288_3029746027615728547_n-sound-bar.jpg", alt: "Soundbar and television integration by J&S Audio Video Solutions" },
  { file: "123817440_117078436859685_6823191246193875934_n_outdoor_tv.jpg", alt: "Outdoor television installation by J&S Audio Video Solutions" },
];

const landscapeGalleryItems: GalleryItem[] = [
  {
    id: "uxbridge-home-theater-system",
    image: supplierImg("uxbridge-home-theater-system"),
    alt: "Wall-mounted television with speakers and an AV cabinet in Uxbridge, MA",
    caption: "Uxbridge, MA",
  },
  {
    id: "palmer-home-theater-tv",
    image: supplierImg("palmer-home-theater-tv"),
    alt: "Wall-mounted television in Palmer, MA",
    caption: "Palmer, MA",
  },
  { file: "j-s-audio-video-solutions-home-theater-tv-mount-600x450.jpg", alt: "Large flat-screen television mounted on a pale living-room wall" },
  { file: "490522160_1172513571552469_2319196569004136287_n-shrewsbury.jpg", alt: "Custom audio video installation in Shrewsbury, MA by J&S Audio Video Solutions" },
  { file: "486156498_1154897169980776_4519007158649887290_n-audio-video-leominster.jpg", alt: "Audio video installation project in Leominster, MA by J&S Audio Video Solutions" },
  { file: "J_S_video_equipment_framingham.jpg", alt: "Commercial video equipment installation in Framingham, MA by J&S Audio Video Solutions" },
  { file: "488657372_1165411945595965_2800093043846313905_n-controllers.jpg", alt: "Audio video control system rack built and installed by J&S Audio Video Solutions" },
  { file: "123829016_117078626859666_2446716433118441475_n-wall-mount-restaurant.jpg", alt: "Restaurant wall-mounted television installation by J&S Audio Video Solutions" },
  { file: "event_stage.jpg", alt: "Event stage audio visual setup by J&S Audio Video Solutions" },
  {
    id: "westborough-home-theater-after-1",
    image: supplierImg("westborough-home-theater-after-1"),
    alt: "Television installed in built-in cabinetry in a Westborough, MA living room",
  },
];

const Home: React.FC = () => (
  <>
    <Helmet>
      <title>J&amp;S Audio Video Solutions – AV Equipment Supplier – Central MA</title>
      <meta
        name="description"
        content="J&S Audio Video Solutions, based in Leicester, MA, delivers AV installation services, equipment supply, and technical support for homes, restaurants, and offices throughout Worcester County and the surrounding towns."
      />
      <meta property="og:title" content="J&S Audio Video Solutions – AV Equipment Supplier – Central MA" />
      <meta property="og:description" content="AV installation, equipment supply, and technical support, based in Leicester, MA, serving Worcester County and the surrounding towns." />
      <meta property="og:type" content="website" />
    </Helmet>

    <section className="hero" data-testid="hero">
      <div className="hero-bg" aria-hidden="true">
        <Picture
          src={heroImg}
          alt=""
          width={1600}
          height={900}
          sizes="100vw"
          priority="high"
          lazy={false}
        />
      </div>
      <div className="container hero-content">
        <h1 className="eyebrow" style={{ color: "var(--color-accent)" }}>Audio Visual Equipment Supplier · Central Massachusetts</h1>
        <h2 className="hero-headline">Audio and video done with care. Built to last.</h2>
        <p className="lead">Installation Services, Audio Visual Equipment Supplies, and Technical Support for homeowners, property managers, and businesses across Central Massachusetts.</p>
        <div className="hero-ctas">
          <Link to="/contact" className="btn" data-testid="link-hero-cta">Request a Consultation</Link>
          <a href="#services" className="btn btn-outline" data-testid="link-hero-services">Explore Services</a>
        </div>
      </div>
    </section>

    <section id="about" className="section">
      <div className="container split">
        <div>
          <span className="eyebrow">About J&amp;S</span>
          <h2>The local AV pro your neighbors already called.</h2>
          <p style={{ marginTop: "1rem" }}>
            You want an audio visual equipment supplier that picks up the phone, shows up when promised, and treats your
            space the way they would treat their own. That's the entire point of J&amp;S Audio Video Solutions. We design
            and install systems that fit the room, the budget, and the way you actually live or work, and then we stay
            available for the long tail of <em>technical support</em> that every real system needs.
          </p>
          <p style={{ marginTop: "1rem" }}>
            From a single wall-mounted television to a multi-zone restaurant audio rebuild, that
            has to work the first time, every project gets the same quiet attention to detail. Based in Leicester,
            serving Worcester County and the surrounding towns.
          </p>
          <div style={{ marginTop: "var(--space-4)", display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <Link to="/contact" className="btn" data-testid="link-about-cta">Start a Project</Link>
            <a href="tel:+15084500490" className="btn btn-outline-dark" data-testid="link-about-phone">Call 508-450-0490</a>
          </div>
        </div>
        <div>
          <Picture
            src={aboutImg}
            alt="J&S Audio Video Solutions technician completing a clean television installation in a Worcester County home"
            width={1200}
            height={900}
            className="split-img"
            sizes="(max-width: 768px) 100vw, 600px"
          />
        </div>
      </div>
    </section>

    <section id="services" className="section section-light" data-testid="services-overview">
      <div className="container">
        <div className="center">
          <span className="eyebrow">What we do</span>
          <h2>Design, installation, and support, all in-house.</h2>
          <p className="lead" style={{ maxWidth: 700, margin: "0.75rem auto 0" }}>Whether the project is a home theater, a multi-zone restaurant, or a conference room, the same team designs it, installs it, and supports it. We pride ourselves in the service provide before, during, and after, setting us apart.</p>
        </div>

        <div className="card-grid card-grid-3" style={{ marginTop: "var(--space-6)" }}>
          <article className="card" data-testid="service-card-home-theater">
            <Picture
              src={imgHomeTheater}
              alt="Home theater system installation in Leicester, MA by J&S Audio Video Solutions"
              width={1200}
              height={750}
              className="card-img"
              sizes="(max-width: 768px) 100vw, 400px"
            />
            <div className="card-content">
              <h3 className="card-title">Home Theater Systems</h3>
              <p className="card-desc">Dedicated theater rooms, surround sound, large-format display systems, smart home integration, and acoustic treatment, engineered for the room you actually have.</p>
              <Link to="/services/home-theater-systems" className="card-link" data-testid="link-service-home-theater">Explore Home Theater</Link>
            </div>
          </article>

          <article className="card" data-testid="service-card-sound-systems">
            <Picture
              src={imgSoundSystems}
              alt="Outdoor distributed sound system installation in Worcester, MA by J&S Audio Video Solutions"
              width={1200}
              height={750}
              className="card-img"
              sizes="(max-width: 768px) 100vw, 400px"
            />
            <div className="card-content">
              <h3 className="card-title">Sound Systems</h3>
              <p className="card-desc">Commercial audio, distributed sound, speaker installation, and zone control, tuned so every seat hears the same thing.</p>
              <Link to="/services/sound-systems" className="card-link" data-testid="link-service-sound-systems">Explore Sound Systems</Link>
            </div>
          </article>

          <article className="card" data-testid="service-card-commercial-av">
            <Picture
              src={imgCommercial}
              alt="Modern office AV installation in Marlborough, MA by J&S Audio Video Solutions"
              width={1200}
              height={750}
              className="card-img"
              sizes="(max-width: 768px) 100vw, 400px"
            />
            <div className="card-content">
              <h3 className="card-title">Commercial AV Solutions</h3>
              <p className="card-desc">Conference room AV, video conferencing, and training-room AV. We focus on systems in the office that are easy to use and maintain.</p>
              <Link to="/services/commercial-av-solutions" className="card-link" data-testid="link-service-commercial-av">Explore Commercial AV</Link>
            </div>
          </article>

          <article className="card" data-testid="service-card-wifi-surveillance">
            <div className="card-content">
              <h3 className="card-title">Residential Wi-Fi Surveillance</h3>
              <p className="card-desc">Wireless security cameras selected, placed, and installed so you see what matters. Coverage planned for your property, remote viewing on your phone.</p>
              <Link to="/services/residential-wifi-surveillance" className="card-link" data-testid="link-service-wifi-surveillance">Explore Wi-Fi Surveillance</Link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section className="section" data-testid="recent-work">
      <div className="container">
        <div className="center">
          <span className="eyebrow">Recent work</span>
          <h2>A sample of recent installs.</h2>
          <p className="lead" style={{ maxWidth: 700, margin: "0.75rem auto 0" }}>
            Living rooms, restaurants, offices, outdoor spaces, and event stages, real installs by the team that quietly
            answers the phone after the job is done.
          </p>
        </div>
        <Gallery items={landscapeGalleryItems} className="gallery gallery-landscape" testId="home-gallery-landscape" />
        <Gallery items={portraitGalleryItems} className="gallery gallery-continued" testId="home-gallery" />
      </div>
    </section>

    <section className="section section-light">
      <div className="container">
        <div className="center">
          <span className="eyebrow">How we work</span>
          <h2>A simple process, taken seriously.</h2>
        </div>
        <ProcessStrip />
      </div>
    </section>

    <Testimonials />

    <CTABand
      heading="Bring us the room. We'll bring the rest."
      body="Estimates are free. We'll talk through what you actually need before quoting anything."
    />
  </>
);

export default Home;
